import type { FC, ReactNode } from 'react';
import React, { useCallback, useMemo } from 'react';
import { Box, Flex, styled } from '@withjoy/joykit';
import { buildLowResUrl } from '@apps/guest/packages/layout-engine/components/OptimizedEventGuestSitePhoto/renditions';
import { useSizeObserver } from '@shared/utils/hooks/useSizeObserver';
import { PHOTO_PRESENTATION_CONFIG } from '@shared/utils/photoPresentationLayout';
import { SsrLazyScreen } from '@apps/guest/packages/layout-engine/components/SsrLazyScreen';
import { EventNameNavForStandaloneHeader } from '../components/EventNameNavForStandaloneHeader/EventNameNavForStandaloneHeader';
import { pxToRem } from '@withjoy/joykit/theme';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { BouncingChevron } from '../components/BouncingChevron/BouncingChevron';
import { InlineEditor } from '@apps/guest/shared/components/InlineEditor';
import { useTranslation } from '@shared/core';
import { isInIframe } from '@shared/utils/isInIframe';
import { OptimizedEventGuestSitePhoto, getSsrHeroSizes } from '@apps/guest/packages/layout-engine/components/OptimizedEventGuestSitePhoto';
import { PhotoBackgroundRenderer } from './PhotoBackgroundRenderer';
import { useIsCompactBrannanHeader } from '../hooks/useIsCompactBrannanHeader';
import { MonogramByContainerId, MONOGRAM_SIZE } from './MonogramContent';
import { MonogramEventDisplayName } from './MonogramEventDisplayName';
import { useHandleEditMonogramClick } from './useHandleEditMonogramClick';
import type { PhotoBackgroundMonogramLayoutProps } from './PhotoBackgroundMonogram.types';

/* Sits above art sprites (docked+1) that live inside FrameOuter, which is a sibling
   rendered after StandaloneWebsiteHeader in the DOM. */
const PhotoBackgroundOuter = styled(Box)`
  z-index: ${({ theme }) => theme.zIndices.docked + 2};
`;

const CONTAINER_ASPECT_RATIO = PHOTO_PRESENTATION_CONFIG.photoBackgroundMonogram.aspectRatio as number;
const CONTAINER_MOBILE_ASPECT_RATIO = PHOTO_PRESENTATION_CONFIG.photoBackgroundMonogram.mobileAspectRatio as number;

const isPreviewing = isInIframe();

export const PhotoBackgroundMonogramLayout: FC<PhotoBackgroundMonogramLayoutProps> = props => {
  const {
    websiteHeader,
    showSkeleton,
    currentPageType,
    isInlineEditing = false,
    renderedContainerHeight,
    renderedContainerWidth,
    editableContentRef,
    forceRenderViewIn,
    scaleValue = 1,
    placeholderImageUrl,
    onTextAlignmentChange,
    pages,
    pageSlug,
    ...restProps
  } = props;

  const photo = websiteHeader.headerPhoto.photos?.[0];
  const { navigationStyle, jsonContainerId, monogramColor, monogramEventName } = websiteHeader.headerPhoto;

  const [containerDimensions, containerRef] = useSizeObserver();

  const [isMobile = false] = useResponsive({ values: { xxs: true, sm: false } });
  const showEventDisplayName = !!monogramEventName?.showEventDisplayName && !!monogramEventName.content;
  const deviceSpecificContainerAspectRatio = isMobile && photo?.mobileCropInfo ? CONTAINER_MOBILE_ASPECT_RATIO : CONTAINER_ASPECT_RATIO;

  const { isCompactView } = useIsCompactBrannanHeader({
    pages,
    pageSlug,
    currentPageType,
    brannanNavigation: restProps.eventDesign?.brannanNavigation,
  });

  const shouldHidePhotoBackground = useMemo(() => {
    return isCompactView && restProps.navBarVariant === 'menu';
  }, [isCompactView, restProps.navBarVariant]);

  const { tq } = useTranslation('inlineEditor');

  const { height, aspectRatio } = useMemo(
    () =>
      isCompactView
        ? { height: '100%', aspectRatio: 'unset' }
        : {
            height: isInlineEditing && renderedContainerHeight ? `${renderedContainerHeight}px` : isMobile ? '90svh' : '100svh',
            aspectRatio: `${deviceSpecificContainerAspectRatio}`,
          },
    [deviceSpecificContainerAspectRatio, isCompactView, isInlineEditing, isMobile, renderedContainerHeight],
  );

  const viewportHeightFallback = isMobile ? '90svh' : '100svh';

  const { containerAspectRatio, containerHeight, containerMinHeight, containerOverflow } = useMemo(() => {
    return {
      containerAspectRatio: shouldHidePhotoBackground ? 'unset' : aspectRatio,
      containerHeight: shouldHidePhotoBackground ? 'auto' : height,
      containerMinHeight: shouldHidePhotoBackground ? 'auto' : pxToRem(96),
      containerOverflow: shouldHidePhotoBackground || isInlineEditing ? 'visible' : 'hidden',
    };
  }, [shouldHidePhotoBackground, aspectRatio, height, isInlineEditing]);

  const handleEditPhotoClick = useEventCallback(() => {
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editHeaderPhoto' },
    });
  });

  const handleEditMonogramClick = useHandleEditMonogramClick();

  const renderPhotoWrapper = useCallback(
    (photoElement: ReactNode) => (
      <InlineEditor
        elementLabel={tq`Photo`}
        wrapperType="actionInside"
        actionData={{ editPhoto: handleEditPhotoClick }}
        componentName="headerPhoto"
        pageName={currentPageType}
        pageSlug={pageSlug || ''}
        wrapperCSS={{ width: '100%', height: '100%' }}
        actionInsideVerticalAlign="top"
        isEligibleForInlineEditing={!isCompactView}
      >
        {photoElement}
      </InlineEditor>
    ),
    [tq, handleEditPhotoClick, currentPageType, pageSlug, isCompactView],
  );

  const eventNameNavProps = {
    eventDisplayName: websiteHeader.eventDisplayName,
    colors: websiteHeader.headerPhoto.colors,
    navigationStyle,
    // Monogram is the focal element; the event display name (hero title) is intentionally hidden,
    // so there is no inline title-edit action to wire for this layout.
    hideEventName: true,
    isCompactView,
    currentPageType,
    isContentEditable: isInlineEditing,
    editableContentRef,
    forceRenderViewIn,
    scaleValue,
    pages,
    pageSlug,
    renderedContainerHeight,
    renderedContainerWidth,
    viewportHeightFallback,
    onTextAlignmentChange,
    ...restProps,
  };

  // Centered monogram overlaid on the background photo, with the optional event display name
  // stacked directly below it. Title/nav still render via EventNameNavForStandaloneHeader.
  // Hidden in compact view (classic nav on non-welcome pages), where the header collapses.
  const monogramOverlay =
    !isCompactView && (jsonContainerId || showEventDisplayName) ? (
      <Flex
        position="absolute"
        top={0}
        left={0}
        width="100%"
        height="100%"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        zIndex="banner"
        pointerEvents="none"
      >
        {jsonContainerId && (
          <InlineEditor
            elementLabel={tq`Monogram`}
            wrapperType="actionInside"
            actionData={{ editMonogram: handleEditMonogramClick }}
            componentName="headerPhoto"
            pageName={currentPageType}
            pageSlug={pageSlug || ''}
            // Caps wrapper at 432px (MonogramView's maxWidth), preventing hover from firing across
            // the full-width centering overlay. min() keeps it responsive on narrow screens.
            // pointerEvents:auto re-enables hover here; the surrounding overlay stays pointerEvents="none".
            wrapperCSS={{ width: `min(${MONOGRAM_SIZE}px, 100%)`, pointerEvents: 'auto' }}
          >
            <MonogramByContainerId
              jsonContainerId={jsonContainerId}
              showPlaceholder={showSkeleton}
              fontColor={monogramColor}
            />
          </InlineEditor>
        )}
        {showEventDisplayName && (
          <MonogramEventDisplayName
            content={monogramEventName?.content ?? ''}
            color={monogramEventName?.color ?? monogramColor}
          />
        )}
      </Flex>
    ) : null;

  return (
    <PhotoBackgroundOuter
      position="relative"
      width="100%"
    >
      <Flex
        ref={containerRef}
        position="relative"
        width="100%"
        aspectRatio={containerAspectRatio}
        height={containerHeight}
        minHeight={containerMinHeight}
        overflow={containerOverflow}
      >
        {shouldHidePhotoBackground ? (
          <EventNameNavForStandaloneHeader {...eventNameNavProps} />
        ) : (
          <SsrLazyScreen
            src={buildLowResUrl(photo?.url || placeholderImageUrl || '')}
            width="100%"
            height={height}
            aspectRatio={aspectRatio}
            skeletonGroupOverrides={{
              Root: {
                props: {
                  width: '100%',
                  height: height,
                  aspectRatio: aspectRatio,
                },
              },
              PlaceholderRoot: {
                props: {
                  width: '100%',
                  height: height,
                  aspectRatio: aspectRatio,
                  position: 'absolute',
                },
              },
            }}
            showSkeleton={showSkeleton}
          >
            {() => (
              <PhotoBackgroundRenderer
                photo={photo}
                placeholderImageUrl={placeholderImageUrl}
                containerDimensions={containerDimensions}
                height={height}
                isCompactView={isCompactView}
                renderedContainerHeight={renderedContainerHeight}
                renderedContainerWidth={renderedContainerWidth}
                isMobile={isMobile}
                deviceSpecificContainerAspectRatio={deviceSpecificContainerAspectRatio}
                photoRenderer={OptimizedEventGuestSitePhoto}
                renderPhotoWrapper={renderPhotoWrapper}
                // The compact header (~96px, classic-navigation non-welcome routes) must not compete
                // with page content for a high-priority, viewport-sized fetch — only the full-size
                // hero gets the eager SSR sizes.
                ssrSizes={isCompactView ? undefined : getSsrHeroSizes('photoBackgroundMonogram')}
              >
                {monogramOverlay}
                <Box
                  width="100%"
                  height="100%"
                  position={isCompactView ? 'relative' : 'absolute'}
                  zIndex="banner"
                  pointerEvents={isPreviewing ? 'none' : 'auto'}
                >
                  <EventNameNavForStandaloneHeader {...eventNameNavProps} />
                </Box>
              </PhotoBackgroundRenderer>
            )}
          </SsrLazyScreen>
        )}
      </Flex>
      {isMobile && !isCompactView && !shouldHidePhotoBackground && !isInlineEditing && <BouncingChevron />}
    </PhotoBackgroundOuter>
  );
};

PhotoBackgroundMonogramLayout.displayName = 'PhotoBackgroundMonogramLayout';
